import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { SetupStatusResponse } from '@/types';
import { canReadSetupStatus } from '@/providers/accessControlProvider';
import { getImpersonatedRental, getStoredUser } from '@/services/auth';
import { fetchSetupStatus } from '../core/setupStatusService';

// The prefix of every user's key: invalidating it refreshes the status of whoever is signed in.
export const SETUP_STATUS_QUERY_KEY_PREFIX = ['setup-status'] as const;

// The QueryClient outlives a logout and a login in one tab, so the key names the user and
// the Rental. A user who may not read gets a key nothing ever fills: no cached data of
// another session leaks to them. Reads the stored session, like canReadSetupStatus.
export const setupStatusQueryKey = (): ReadonlyArray<string | number | null> => {
  const user = getStoredUser();
  const canRead = canReadSetupStatus();
  const rentalId = getImpersonatedRental()?.id ?? user?.rental?.id ?? null;
  return [
    ...SETUP_STATUS_QUERY_KEY_PREFIX,
    canRead ? (user?.id ?? null) : null,
    canRead ? rentalId : null,
  ];
};

// An employee gets a 403 and Global view has no rental, so neither fetches.
export const useSetupStatus = (): UseQueryResult<SetupStatusResponse, Error> =>
  useQuery({
    queryKey: setupStatusQueryKey(),
    queryFn: fetchSetupStatus,
    enabled: canReadSetupStatus(),
  });
